import { supabase } from '@/lib/supabase'
import {
  extractCommunityMessagePlainText,
  prepareCommunityMessageContent,
} from '../utils/communityMessageContent'

export type CommunityAvatar = {
  authorId: string
  authorName: string
  authorAvatarUrl: string | null
}

export type CommunitySummary = {
  opportunityId: number
  messagesCount: number
  lastMessageAt: string | null
  avatars: CommunityAvatar[]
}

export type CommunitySummaryMap = Record<string, CommunitySummary>

export type CommunityReaction = {
  emoji: string
  count: number
  reactedByCurrentUser: boolean
}

export type CommunityMessageAttachment = {
  id: number
  messageId: number
  url: string
  storageBucket: string | null
  storagePath: string | null
  contentType: string | null
  sizeBytes: number | null
  createdAt: string
}

export type CommunityMessage = {
  id: number
  opportunityId: number
  authorId: string
  authorName: string
  authorAvatarUrl: string | null
  content: string
  createdAt: string
  reactions: CommunityReaction[]
  attachments: CommunityMessageAttachment[]
}

export type CommunityThread = {
  messages: CommunityMessage[]
  cardReactions: CommunityReaction[]
}

function normalizeAuthorName(raw: string | null | undefined): string {
  const value = String(raw || '').trim()
  if (!value) return 'Usuário'
  if (value.includes('@')) {
    const [before] = value.split('@')
    const cleaned = before.replace(/[._]+/g, ' ')
    const parts = cleaned.split(/\s+/).filter(Boolean)
    if (!parts.length) return value
    return parts
      .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : ''))
      .filter(Boolean)
      .join(' ')
  }
  return value
}

/*
function normalizePlainTextWithLineBreaks(input: string): string {
  const text = String(input || '')
    .replace(/\u00A0/g, ' ')
    .replace(/&nbsp;/gi, ' ')

  // Quebra em linhas considerando todos os formatos de newline (\n, \r\n, \r)
  const rawLines = text.split(/\r\n|\r|\n/)

  // Remove linhas vazias apenas no início/fim, preservando espaços entre parágrafos
  let start = 0
  let end = rawLines.length
  while (start < end && !rawLines[start].trim()) start += 1
  while (end > start && !rawLines[end - 1].trim()) end -= 1

  const sliced = rawLines.slice(start, end)

  // Normaliza apenas espaços/tabs dentro de cada linha, preservando as quebras
  const normalizedLines = sliced.map((line) => line.replace(/[ \t]+/g, ' ').trimEnd())

  return normalizedLines.join('\n').trim()
}
*/

export function sanitizeCommunityMessageContent(input: string): string {
  return extractCommunityMessagePlainText(input)

  /*
  const raw = String(input || '').trim()
  if (!raw) return ''

  // Se não houver tags aparentes, mantém o texto original
  if (!/[<>&]/.test(raw)) return raw

  try {
    if (false && typeof document !== 'undefined') {
      const div = document.createElement('div')
      div.innerHTML = raw
      const text = (div as HTMLElement).innerText || div.textContent || ''
      return normalizePlainTextWithLineBreaks(text)
    }
  } catch {
    // fallback para ambientes sem DOM
  }

  const withBreaks = raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6]|blockquote)\s*>/gi, '\n')

  const withoutTags = withBreaks.replace(/<[^>]+>/g, ' ')

  return normalizePlainTextWithLineBreaks(withoutTags)
  */
}

function normalizeIds(raw: number[]): number[] {
  const unique = Array.from(
    new Set(
      (raw || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id)),
    ),
  )
  return unique
}

export function aggregateCardReactions(
  rows: any,
  currentUserId: string | null,
): CommunityReaction[] {
  const cardReactionMap: Record<string, CommunityReaction> = {}
  const cardRows = Array.isArray(rows) ? rows : []

  cardRows.forEach((row: any) => {
    const emoji = String(row.emoji || '').trim()
    if (!emoji) return
    let rec = cardReactionMap[emoji]
    if (!rec) {
      rec = { emoji, count: 0, reactedByCurrentUser: false }
      cardReactionMap[emoji] = rec
    }
    rec.count += 1
    if (currentUserId && String(row.user_id) === currentUserId) {
      rec.reactedByCurrentUser = true
    }
  })

  return Object.values(cardReactionMap).sort((a, b) => b.count - a.count)
}

export async function fetchCommunityEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_community_enabled' as any)
    if (error) {
      // eslint-disable-next-line no-console
      console.error('user_community_enabled error', error)
      return false
    }
    return Boolean(data)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('user_community_enabled error', e)
    return false
  }
}

export async function fetchCommunitySummary(opportunityIds: number[]): Promise<CommunitySummaryMap> {
  const ids = normalizeIds(opportunityIds)
  if (!ids.length) return {}

  let currentUserId: string | null = null
  let currentUserMetaAvatar: string | null = null
  try {
    const auth = await supabase.auth.getUser()
    const user = auth.data.user
    currentUserId = user?.id ? String(user.id) : null
    currentUserMetaAvatar = (user?.user_metadata as any)?.avatar_url ?? null
  } catch {
    // Ignora falhas de auth; seguimos apenas com snapshots/avatars do resumo
  }

  const { data, error } = await supabase.rpc('opportunity_community_summary' as any, {
    p_opportunity_ids: ids,
  })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('opportunity_community_summary error', error)
    return {}
  }

  const map: CommunitySummaryMap = {}
  const rows = Array.isArray(data) ? data : []

  // Coleta todos os autores do resumo para aplicar override a partir de profiles
  const avatarAuthorIds = new Set<string>()
  rows.forEach((row: any) => {
    const avatarsJson = row.avatars as any
    if (!Array.isArray(avatarsJson)) return
    avatarsJson.forEach((a: any) => {
      const id = String(a.author_id || a.authorId || '').trim()
      if (id) avatarAuthorIds.add(id)
    })
  })

  const profilesMap: Record<
    string,
    {
      name: string
      avatarUrl: string | null
    }
  > = {}

  if (avatarAuthorIds.size > 0) {
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles' as any)
        .select('id,full_name,avatar_url')
        .in('id', Array.from(avatarAuthorIds) as any)
      if (profilesError) {
        // eslint-disable-next-line no-console
        console.error('fetchCommunitySummary profiles error', profilesError)
      } else if (Array.isArray(profiles)) {
        profiles.forEach((p: any) => {
          const id = p.id ? String(p.id) : ''
          if (!id) return
          profilesMap[id] = {
            name: String(p.full_name || '').trim(),
            avatarUrl:
              typeof p.avatar_url === 'string' || p.avatar_url === null
                ? (p.avatar_url as string | null)
                : null,
          }
        })
      }
    } catch {
      // Em caso de erro, resumimos apenas com snapshots do resumo
    }
  }

  rows.forEach((row: any) => {
    const oppId = Number(row.opportunity_id)
    if (!Number.isFinite(oppId)) return
    const messagesCount = Number(row.messages_count ?? 0)
    const lastMessageAt = row.last_message_at ? String(row.last_message_at) : null
    const avatarsJson = row.avatars as any
    const avatars: CommunityAvatar[] = Array.isArray(avatarsJson)
      ? avatarsJson.map((a) => {
          const authorId = String(a.author_id || a.authorId || '')
          const profileOverride = profilesMap[authorId]

          const rawName =
            profileOverride?.name && profileOverride.name.trim().length > 0
              ? profileOverride.name
              : a.author_name || a.authorName

          let rawAvatar: string | null =
            typeof a.author_avatar_url === 'string' || a.author_avatar_url === null
              ? (a.author_avatar_url as string | null)
              : null

          if (profileOverride && profileOverride.avatarUrl !== undefined) {
            rawAvatar = profileOverride.avatarUrl
          }

          if (currentUserId && authorId === currentUserId && currentUserMetaAvatar) {
            rawAvatar = currentUserMetaAvatar
          }

          return {
            authorId,
            authorName: normalizeAuthorName(rawName || ''),
            authorAvatarUrl: rawAvatar,
          }
        })
      : []

    map[String(oppId)] = {
      opportunityId: oppId,
      messagesCount: Number.isFinite(messagesCount) && messagesCount >= 0 ? messagesCount : 0,
      lastMessageAt,
      avatars,
    }
  })

  return map
}

export async function fetchCommunityThread(opportunityId: number): Promise<CommunityThread> {
  const oppId = Number(opportunityId)
  if (!Number.isFinite(oppId)) return { messages: [], cardReactions: [] }

  const auth = await supabase.auth.getUser()
  const user = auth.data.user
  const currentUserId = user?.id ? String(user.id) : null
  const currentUserMetaAvatar: string | null =
    (user?.user_metadata as any)?.avatar_url ?? null

  const [
    { data: msgData, error: msgError },
    { data: cardReactData, error: cardReactError },
  ] = await Promise.all([
    supabase
      .from('opportunity_community_messages')
      .select('id,opportunity_id,author_id,author_name,author_avatar_url,content,created_at')
      .eq('opportunity_id', oppId)
      .order('created_at', { ascending: true }),
    supabase
      .from('opportunity_community_reactions')
      .select('opportunity_id,user_id,emoji')
      .eq('opportunity_id', oppId),
  ])

  if (msgError) {
    // eslint-disable-next-line no-console
    console.error('fetchCommunityThread messages error', msgError)
    return { messages: [], cardReactions: [] }
  }

  const messagesRows = Array.isArray(msgData) ? msgData : []
  const messageIds = messagesRows.map((r: any) => Number(r.id)).filter((id) => Number.isFinite(id))
  const authorIds = Array.from(
    new Set(
      messagesRows
        .map((r: any) => (r.author_id ? String(r.author_id) : ''))
        .filter((id) => !!id),
    ),
  )
  const attachmentsByMessageId: Record<string, CommunityMessageAttachment[]> = {}

  // Mapa com dados atuais do perfil para garantir SSOT de nome e avatar
  const profilesMap: Record<
    string,
    {
      name: string
      avatarUrl: string | null
    }
  > = {}

  if (authorIds.length > 0) {
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles' as any)
      .select('id,full_name,avatar_url')
      .in('id', authorIds as any)

    if (profilesError) {
      // eslint-disable-next-line no-console
      console.error('fetchCommunityThread profiles error', profilesError)
    } else if (Array.isArray(profiles)) {
      profiles.forEach((p: any) => {
        const id = p.id ? String(p.id) : ''
        if (!id) return
        profilesMap[id] = {
          name: String(p.full_name || '').trim(),
          avatarUrl:
            typeof p.avatar_url === 'string' || p.avatar_url === null
              ? (p.avatar_url as string | null)
              : null,
        }
      })
    }
  }

  if (messageIds.length > 0) {
    try {
      const { data: attachmentsData, error: attachmentsError } = await supabase
        .from('opportunity_community_message_attachments' as any)
        .select(
          'id,message_id,url,storage_bucket,storage_path,content_type,size_bytes,created_at',
        )
        .in('message_id', messageIds as any)

      if (attachmentsError) {
        // eslint-disable-next-line no-console
        console.error('fetchCommunityThread attachments error', attachmentsError)
      } else if (Array.isArray(attachmentsData)) {
        attachmentsData.forEach((row: any) => {
          const mid = Number(row.message_id)
          if (!Number.isFinite(mid)) return
          const key = String(mid)

          const attachment: CommunityMessageAttachment = {
            id: Number(row.id),
            messageId: mid,
            url: String(row.url || ''),
            storageBucket:
              typeof row.storage_bucket === 'string' || row.storage_bucket === null
                ? (row.storage_bucket as string | null)
                : null,
            storagePath:
              typeof row.storage_path === 'string' || row.storage_path === null
                ? (row.storage_path as string | null)
                : null,
            contentType:
              typeof row.content_type === 'string' || row.content_type === null
                ? (row.content_type as string | null)
                : null,
            sizeBytes:
              row.size_bytes === null || row.size_bytes === undefined
                ? null
                : Number.isFinite(Number(row.size_bytes))
                  ? Number(row.size_bytes)
                  : null,
            createdAt: row.created_at ? String(row.created_at) : '',
          }

          if (!attachmentsByMessageId[key]) attachmentsByMessageId[key] = []
          attachmentsByMessageId[key].push(attachment)
        })
      }
    } catch (attachmentsErr) {
      // eslint-disable-next-line no-console
      console.error('fetchCommunityThread attachments unexpected error', attachmentsErr)
    }
  }

  let reactionsRows: any[] = []
  if (messageIds.length > 0) {
    const { data: reacts, error: reactsErr } = await supabase
      .from('opportunity_community_message_reactions')
      .select('message_id,user_id,emoji')
      .in('message_id', messageIds)
    if (reactsErr) {
      // eslint-disable-next-line no-console
      console.error('fetchCommunityThread reactions error', reactsErr)
      reactionsRows = []
    } else {
      reactionsRows = Array.isArray(reacts) ? reacts : []
    }
  }
  if (cardReactError) {
    // eslint-disable-next-line no-console
    console.error('fetchCommunityThread card reactions error', cardReactError)
  }

  const byMessage: Record<string, CommunityReaction[]> = {}
  reactionsRows.forEach((row: any) => {
    const mid = Number(row.message_id)
    if (!Number.isFinite(mid)) return
    const key = String(mid)
    const emoji = String(row.emoji || '').trim()
    if (!emoji) return
    if (!byMessage[key]) byMessage[key] = []
    const arr = byMessage[key]
    let rec = arr.find((r) => r.emoji === emoji)
    if (!rec) {
      rec = { emoji, count: 0, reactedByCurrentUser: false }
      arr.push(rec)
    }
    rec.count += 1
    if (currentUserId && String(row.user_id) === currentUserId) {
      rec.reactedByCurrentUser = true
    }
  })

  // Avatar mais recente conhecido no contexto deste thread para cada autor
  // (messagesRows estÁ ordenado por created_at ascendente, entÄo o ºltimo nÄo-nulo ganha)
  const latestAvatarByAuthorId: Record<string, string | null> = {}
  messagesRows.forEach((row: any) => {
    const authorId = row.author_id ? String(row.author_id) : ''
    if (!authorId) return
    const avatarValue =
      typeof row.author_avatar_url === 'string' || row.author_avatar_url === null
        ? (row.author_avatar_url as string | null)
        : null
    if (avatarValue) {
      latestAvatarByAuthorId[authorId] = avatarValue
    }
  })

  const messages: CommunityMessage[] = messagesRows.map((row: any) => {
    const id = Number(row.id)
    const key = String(id)
    const authorId = String(row.author_id)

    const profileOverride = profilesMap[authorId]
    const rawName =
      profileOverride?.name && profileOverride.name.trim().length > 0
        ? profileOverride.name
        : row.author_name

    // Prioriza o avatar mais recente das mensagens do thread; se nÄo houver, usa o avatar do perfil;
    // por ºltimo, cai no snapshot salvo na prÛpria mensagem.
    const threadAvatar = Object.prototype.hasOwnProperty.call(latestAvatarByAuthorId, authorId)
      ? latestAvatarByAuthorId[authorId]
      : null

    let rawAvatar: string | null | undefined =
      threadAvatar !== null && threadAvatar !== undefined && threadAvatar !== ''
        ? threadAvatar
        : profileOverride && profileOverride.avatarUrl !== undefined
          ? profileOverride.avatarUrl
          : row.author_avatar_url

    // Se a mensagem for do usuÁrio logado, prioriza sempre o avatar atual do auth.user_metadata
    // para refletir imediatamente a foto configurada no perfil, mesmo antes de snapshots/updates.
    if (currentUserId && authorId === currentUserId && currentUserMetaAvatar) {
      rawAvatar = currentUserMetaAvatar
    }

    return {
      id,
      opportunityId: Number(row.opportunity_id),
      authorId,
      authorName: normalizeAuthorName(rawName),
      authorAvatarUrl:
        typeof rawAvatar === 'string' || rawAvatar === null ? (rawAvatar as string | null) : null,
      content: prepareCommunityMessageContent(String(row.content || '')).html,
      createdAt: row.created_at ? String(row.created_at) : '',
      reactions: (byMessage[key] || []).sort((a, b) => b.count - a.count),
      attachments: (attachmentsByMessageId[key] || []).sort((a, b) => a.id - b.id),
    }
  })

  const cardReactions = aggregateCardReactions(cardReactData, currentUserId)

  return { messages, cardReactions }
}

export async function fetchCommunityCardReactions(
  opportunityId: number,
): Promise<CommunityReaction[]> {
  const oppId = Number(opportunityId)
  if (!Number.isFinite(oppId)) return []

  let currentUserId: string | null = null
  try {
    const auth = await supabase.auth.getUser()
    const user = auth.data.user
    currentUserId = user?.id ? String(user.id) : null
  } catch {
    currentUserId = null
  }

  const { data, error } = await supabase
    .from('opportunity_community_reactions')
    .select('opportunity_id,user_id,emoji')
    .eq('opportunity_id', oppId)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('fetchCommunityCardReactions error', error)
    return []
  }

  return aggregateCardReactions(data, currentUserId)
}

export async function deleteCommunityMessage(messageId: number): Promise<void> {
  const id = Number(messageId)
  if (!Number.isFinite(id) || id <= 0) return
  const { error } = await supabase
    .from('opportunity_community_messages')
    .delete()
    .eq('id', id)
  if (error) {
    // eslint-disable-next-line no-console
    console.error('deleteCommunityMessage error', error)
  }
}

export async function updateCommunityMessage(
  messageId: number,
  content: string,
): Promise<CommunityMessage | null> {
  const id = Number(messageId)
  if (!Number.isFinite(id) || id <= 0) return null

  const prepared = prepareCommunityMessageContent(content)
  if (!prepared.text) return null

  const { data, error } = await supabase
    .from('opportunity_community_messages')
    .update({ content: prepared.html } as any)
    .eq('id', id)
    .select('id,opportunity_id,author_id,author_name,author_avatar_url,content,created_at')
    .single()

  if (error) {
    // eslint-disable-next-line no-console
    console.error('updateCommunityMessage error', error)
    return null
  }

  const row: any = data
  return {
    id: Number(row.id),
    opportunityId: Number(row.opportunity_id),
    authorId: String(row.author_id),
    authorName: normalizeAuthorName(row.author_name),
    authorAvatarUrl:
      typeof row.author_avatar_url === 'string' || row.author_avatar_url === null
        ? (row.author_avatar_url as string | null)
        : null,
    content: prepared.html,
    createdAt: row.created_at ? String(row.created_at) : '',
    reactions: [],
    attachments: [],
  }
}

export async function postCommunityMessage(
  opportunityId: number,
  content: string,
  authorName: string,
  authorAvatarUrl?: string | null,
): Promise<CommunityMessage | null> {
  const prepared = prepareCommunityMessageContent(content)

  const auth = await supabase.auth.getUser()
  const user = auth.data.user
  const userId = user?.id
  if (!userId) throw new Error('Usuário não autenticado')

  const { data, error } = await supabase
    .from('opportunity_community_messages')
    .insert(
      {
        opportunity_id: Number(opportunityId),
        author_id: userId,
        author_name: normalizeAuthorName(
          authorName ||
            ((user?.user_metadata as any)?.full_name as string | null) ||
            (user?.email as string | null) ||
            'Usuário',
        ),
        author_avatar_url: authorAvatarUrl ?? null,
        content: prepared.html,
      } as any,
    )
    .select('id,opportunity_id,author_id,author_name,author_avatar_url,content,created_at')
    .single()

  if (error) {
    // eslint-disable-next-line no-console
    console.error('postCommunityMessage error', error)
    throw error
  }

  const row: any = data
  return {
    id: Number(row.id),
    opportunityId: Number(row.opportunity_id),
    authorId: String(row.author_id),
    authorName: normalizeAuthorName(row.author_name),
    authorAvatarUrl:
      typeof row.author_avatar_url === 'string' || row.author_avatar_url === null
        ? (row.author_avatar_url as string | null)
        : null,
    content: prepared.html,
    createdAt: row.created_at ? String(row.created_at) : '',
    reactions: [],
    attachments: [],
  }
}

export async function addCommunityMessageAttachments(
  messageId: number,
  items: {
    url: string
    storageBucket?: string | null
    storagePath?: string | null
    contentType?: string | null
    sizeBytes?: number | null
  }[],
): Promise<CommunityMessageAttachment[]> {
  const id = Number(messageId)
  if (!Number.isFinite(id) || id <= 0) return []

  const rows = (items || [])
    .map((item) => {
      const url = String(item.url || '').trim()
      if (!url) return null
      return {
        message_id: id,
        url,
        storage_bucket: item.storageBucket ?? null,
        storage_path: item.storagePath ?? null,
        content_type: item.contentType ?? null,
        size_bytes:
          typeof item.sizeBytes === 'number' && Number.isFinite(item.sizeBytes)
            ? Math.max(0, Math.floor(item.sizeBytes))
            : item.sizeBytes == null
              ? null
              : Number(item.sizeBytes) || null,
      }
    })
    .filter(Boolean) as any[]

  if (!rows.length) return []

  const { data, error } = await supabase
    .from('opportunity_community_message_attachments' as any)
    .insert(rows as any)
    .select(
      'id,message_id,url,storage_bucket,storage_path,content_type,size_bytes,created_at',
    )

  if (error) {
    // eslint-disable-next-line no-console
    console.error('addCommunityMessageAttachments error', error)
    throw error
  }

  const out: CommunityMessageAttachment[] = Array.isArray(data)
    ? (data as any[]).map((row) => ({
        id: Number(row.id),
        messageId: Number(row.message_id),
        url: String(row.url || ''),
        storageBucket:
          typeof row.storage_bucket === 'string' || row.storage_bucket === null
            ? (row.storage_bucket as string | null)
            : null,
        storagePath:
          typeof row.storage_path === 'string' || row.storage_path === null
            ? (row.storage_path as string | null)
            : null,
        contentType:
          typeof row.content_type === 'string' || row.content_type === null
            ? (row.content_type as string | null)
            : null,
        sizeBytes:
          row.size_bytes === null || row.size_bytes === undefined
            ? null
            : Number.isFinite(Number(row.size_bytes))
              ? Number(row.size_bytes)
              : null,
        createdAt: row.created_at ? String(row.created_at) : '',
      }))
    : []

  return out
}

export async function toggleMessageReaction(messageId: number, emoji: string): Promise<void> {
  const trimmed = String(emoji || '').trim()
  if (!trimmed) return

  const auth = await supabase.auth.getUser()
  const userId = auth.data.user?.id
  if (!userId) throw new Error('Usuário não autenticado')

  const { data, error } = await supabase
    .from('opportunity_community_message_reactions')
    .select('message_id,user_id,emoji')
    .eq('message_id', Number(messageId))
    .eq('user_id', userId)
    .eq('emoji', trimmed)
    .maybeSingle()

  // Debug: acompanhar resultado do toggle em mensagem
  // eslint-disable-next-line no-console
  console.log('[Community] toggleMessageReaction result', {
    messageId: Number(messageId),
    emoji: trimmed,
    userId,
    hasData: !!data,
    error,
  })

  if (error && String(error.code || '') !== 'PGRST116') {
    // eslint-disable-next-line no-console
    console.error('toggleMessageReaction select error', error)
  }

  if (data) {
    const { error: delError } = await supabase
      .from('opportunity_community_message_reactions')
      .delete()
      .match({ message_id: Number(messageId), user_id: userId, emoji: trimmed } as any)
    if (delError) {
      // eslint-disable-next-line no-console
      console.error('toggleMessageReaction delete error', delError)
    }
    return
  }

  const { error: insError } = await supabase
    .from('opportunity_community_message_reactions')
    .insert({ message_id: Number(messageId), user_id: userId, emoji: trimmed } as any)
  if (insError) {
    // eslint-disable-next-line no-console
    console.error('toggleMessageReaction insert error', insError)
  }
}

export async function toggleCardReaction(opportunityId: number, emoji: string): Promise<void> {
  const trimmed = String(emoji || '').trim()
  if (!trimmed) return

  const auth = await supabase.auth.getUser()
  const userId = auth.data.user?.id
  if (!userId) throw new Error('Usuário não autenticado')

  const { data, error } = await supabase
    .from('opportunity_community_reactions')
    .select('id,opportunity_id,user_id,emoji')
    .eq('opportunity_id', Number(opportunityId))
    .eq('user_id', userId)
    .eq('emoji', trimmed)
    .maybeSingle()

  if (error && String(error.code || '') !== 'PGRST116') {
    // eslint-disable-next-line no-console
    console.error('toggleCardReaction select error', error)
  }

  if (data) {
    const { error: delError } = await supabase
      .from('opportunity_community_reactions')
      .delete()
      .match({ id: (data as any).id } as any)
    if (delError) {
      // eslint-disable-next-line no-console
      console.error('toggleCardReaction delete error', delError)
    }
    return
  }

  const { error: insError } = await supabase
    .from('opportunity_community_reactions')
    .insert({ opportunity_id: Number(opportunityId), user_id: userId, emoji: trimmed } as any)
  if (insError) {
    // eslint-disable-next-line no-console
    console.error('toggleCardReaction insert error', insError)
  }
}
